/**
 * Folds a linear `KnockoutBracketModel` (rounds ordered outer → Final) into the mirrored tree the
 * bracket renders: each non-final round is split down the middle so its first half feeds the left
 * side and its second half feeds the right side, with the lone Final + third-place play-off in the
 * centre. This is the classic "both halves of the draw meet at the Final" layout.
 *
 * Robust to a partially-drawn tournament: the last round is only treated as the centre Final when
 * it holds exactly one match; otherwise every round is split and `final` is null (the centre shows
 * the trophy placeholder). An odd split puts the extra match on the left.
 */

import type {
  BracketRound,
  KnockoutBracketModel,
  MirroredBracket,
} from '../types/knockoutBracket.types'

function leftHalf(round: BracketRound): BracketRound {
  const cut = Math.ceil(round.matches.length / 2)
  return { ...round, matches: round.matches.slice(0, cut) }
}

function rightHalf(round: BracketRound): BracketRound {
  const cut = Math.ceil(round.matches.length / 2)
  return { ...round, matches: round.matches.slice(cut) }
}

export function buildMirroredBracket(model: KnockoutBracketModel): MirroredBracket {
  const rounds = model.rounds
  const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null

  // The Final is the centre only when the innermost round has collapsed to a single match.
  const hasCentreFinal = lastRound != null && lastRound.matches.length === 1
  const splitRounds = hasCentreFinal ? rounds.slice(0, -1) : rounds
  const final = hasCentreFinal ? lastRound.matches[0] : null

  return {
    left: splitRounds.map(leftHalf),
    right: splitRounds.map(rightHalf),
    final,
    thirdPlace: model.thirdPlace ?? null,
  }
}
